import { AppType } from "@azm/core/enums/app-types";
import { EnvironmentMode } from "@azm/core/enums/environment-mode";
import { IEnvironment } from "@azm/core/enums/environment";

type AppTypeKey = AppType;

const STORAGE_KEYS: Record<
	AppType.Internal,
	{
		tokenPropertyName: string;
		refreshTokenPropertyName: string;
		userRolePropertyName: string;
		appTypePropertyName: string;
		mojLoginDataPropertyName: string;
		lastActivityKey: string;
	}
> = {
	[AppType.Internal]: {
		tokenPropertyName: "legalaffairs-token-int",
		refreshTokenPropertyName: "legalaffairs-refresh-token-int",
		userRolePropertyName: "legalaffairs-user-role-int",
		appTypePropertyName: "legalaffairs-app-type-int",
		mojLoginDataPropertyName: "moj-login-data-int",
		lastActivityKey: "legalaffairs-last-activity-int",
	},
};

const sharedEnvironmentConfig = {
	defaultTheme: "light",
	enableThemeSwitch: false,
	enableAccessibilityTools: false,
	i18n_Configurations: {
		defaultLanguage: "ar",
	},
	isLogoutRedirectUrl: false,
	logoutRedirectUrl: "",
	showChatbot: false,
	enableAiTextAssist: true,
	isDgaFilter: false,
} satisfies Pick<
	IEnvironment,
	| "defaultTheme"
	| "enableThemeSwitch"
	| "enableAccessibilityTools"
	| "i18n_Configurations"
	| "isLogoutRedirectUrl"
	| "logoutRedirectUrl"
	| "showChatbot"
	| "enableAiTextAssist"
	| "isDgaFilter"
>;

export type CreateEnvironmentConfig = {
	production: boolean;
	mode: EnvironmentMode;
	appType: AppTypeKey;
	apiBaseUrl: string;
	encryptKeyParts?: readonly string[];
	showChatbot?: boolean;
	enableAiTextAssist?: boolean;
	enableLookupCache?: boolean;
	lookupCacheTtlMs?: number;
	isDgaFilter?: boolean;
	enableThemeSwitch?: boolean;
	enableAccessibilityTools?: boolean;
	useMockApi?: boolean;
};

export function createEnvironment(config: CreateEnvironmentConfig): IEnvironment {
	const storageKeys = STORAGE_KEYS[AppType.Internal];

	return {
		...sharedEnvironmentConfig,
		production: config.production,
		mode: config.mode,
		defaultAppType: config.appType,
		tokenPropertyName: storageKeys.tokenPropertyName,
		refreshTokenPropertyName: storageKeys.refreshTokenPropertyName,
		userRolePropertyName: storageKeys.userRolePropertyName,
		appTypePropertyName: storageKeys.appTypePropertyName,
		mojLoginDataPropertyName: storageKeys.mojLoginDataPropertyName,
		domainBasedOnAppType: {
			[config.appType]: {
				apiBaseUrl: config.apiBaseUrl,
			},
		},
		idle: {
			enabled: true,
			timeoutSeconds: 10 * 60,
			lastActivityKey: storageKeys.lastActivityKey,
		},
		...(config.encryptKeyParts ? { encryptKeyParts: config.encryptKeyParts } : {}),
		showChatbot: config.showChatbot ?? sharedEnvironmentConfig.showChatbot,
		enableAiTextAssist: config.enableAiTextAssist ?? sharedEnvironmentConfig.enableAiTextAssist,
		enableLookupCache: config.enableLookupCache ?? false,
		lookupCacheTtlMs: config.lookupCacheTtlMs ?? 24 * 60 * 60 * 1000,
		isDgaFilter: config.isDgaFilter ?? sharedEnvironmentConfig.isDgaFilter,
		enableThemeSwitch: config.enableThemeSwitch ?? sharedEnvironmentConfig.enableThemeSwitch,
		enableAccessibilityTools: config.enableAccessibilityTools ?? sharedEnvironmentConfig.enableAccessibilityTools,
		useMockApi: config.useMockApi ?? false,
	};
}
